import { DynamicFormRequiredType, type DynamicFormSettings, type VisibilityCondition } from '@myosh/odin-components';
import i18next from 'i18next';
import type { TextualQuestionnaireFieldProps } from '../../../../@types/forms';
import { dataSettings } from '../../util/common.util';

const recordLinksVisibilityCondition: VisibilityCondition = {
  id: 'recordLinksVisibilityCondition',
  sourceFieldId: 'allowRecordLinksForQuestions',
  fieldVisibilityType: 'FIELD',
  targetValueType: 'BOOLEAN',
  targetValue: true,
  negation: false,
};

export const displayScoresOptions: Array<{ text: string; value: boolean }> = [
  { text: i18next.t('yes'), value: true },
  { text: i18next.t('no'), value: false },
];

export const getTextualQuestionnaireFieldSettings = (formBuilderField: {
  id?: number;
  uuid?: string;
  tqPresetAnswerType?: string;
}): DynamicFormSettings => {
  const answerReadOnly = formBuilderField.tqPresetAnswerType === 'YES_NO_NA';
  const fieldId = formBuilderField.id ?? formBuilderField.uuid;
  return {
    id: 'TextualQuestionnaireFieldSettingsGroup',
    caption: '',
    fields: [
      {
        id: 'showFieldCaption',
        fieldType: 'CHECKBOX',
        fieldName: 'showFieldCaption',
        caption: i18next.t('show-field-caption'),
        required: DynamicFormRequiredType.False,
        permissions: { edit: true, read: true },
        position: 30,
      },
      {
        id: `questions-${fieldId}`,
        fieldType: 'OPTION_BUILDER',
        fieldName: 'questions',
        caption: i18next.t('questions'),
        helpMessage: 'List of questions for the textual questionnaire',
        required: DynamicFormRequiredType.True,
        permissions: { edit: true, read: true },
        dynamicProperties: {
          hideFooter: true,
          removeAllowed: true,
        },
        position: 35,
      },
      {
        id: `answers-${fieldId}`,
        fieldType: 'OPTION_BUILDER',
        fieldName: 'answers',
        caption: i18next.t('answers'),
        helpMessage: 'List of answers for the textual questionnaire',
        required: DynamicFormRequiredType.True,
        permissions: { edit: !answerReadOnly, read: true },
        dynamicProperties: {
          hideFooter: true,
        },
        position: 40,
      },
      {
        id: 'displayScores',
        fieldType: 'OPTIONGROUP',
        fieldName: 'displayScores',
        caption: i18next.t('display-scores'),
        helpMessage: i18next.t('display-scores-help-text'),
        required: DynamicFormRequiredType.True,
        permissions: { edit: true, read: true },
        dataSettings,
        dynamicProperties: {
          data: displayScoresOptions,
          target: displayScoresOptions[1].value,
        },
        position: 45,
      },
      {
        id: 'forEachQuestionDisplayLabel',
        fieldType: 'LABEL',
        fieldName: 'forEachQuestionDisplayLabel',
        caption: i18next.t('for-each-question-display'),
        required: DynamicFormRequiredType.False,
        position: 48,
      },
      {
        id: 'allowTextInputWhileAnsweringQuestions',
        fieldType: 'CHECKBOX',
        fieldName: 'allowTextInputWhileAnsweringQuestions',
        caption: i18next.t('comments'),
        required: DynamicFormRequiredType.False,
        permissions: { edit: true, read: true },
        position: 50,
      },
      {
        id: 'allowAttachmentsForQuestions',
        fieldType: 'CHECKBOX',
        fieldName: 'allowAttachmentsForQuestions',
        caption: i18next.t('attachments'),
        required: DynamicFormRequiredType.False,
        permissions: { edit: true, read: true },
        position: 55,
      },
      {
        id: 'allowRecordLinksForQuestions',
        fieldType: 'CHECKBOX',
        fieldName: 'allowRecordLinksForQuestions',
        caption: i18next.t('linked-records'),
        required: DynamicFormRequiredType.False,
        permissions: { edit: true, read: true },
        position: 60,
      },
      {
        id: 'buttonCaption',
        fieldType: 'TEXTFIELD',
        fieldName: 'buttonCaption',
        caption: i18next.t('button-caption'),
        required: DynamicFormRequiredType.True,
        permissions: { edit: true, read: true },
        position: 65,
      },
    ],
    visibilityConditions: [
      {
        id: 'buttonCaption',
        type: 'TEXTFIELD',
        groups: [
          {
            conditions: [recordLinksVisibilityCondition],
          },
        ],
      },
    ],
  };
};

export const getTextualQuestionnaireData = (field: TextualQuestionnaireFieldProps) => {
  const clonedQuestions = structuredClone(field.questions ?? []);
  const clonedAnswers = structuredClone(field.answers ?? []);
  const sortedQuestions = clonedQuestions.sort((a, b) => a.position - b.position);
  const sortedAnswers = clonedAnswers.sort((a, b) => a.position - b.position);
  const selectedDisplayScoresOption =
    displayScoresOptions.find((option) => option.value === field.displayScores) ?? displayScoresOptions[1];

  return {
    showFieldCaption: field.showFieldCaption ?? true,
    allowTextInputWhileAnsweringQuestions: field.allowTextInputWhileAnsweringQuestions ?? false,
    allowAttachmentsForQuestions: field.allowAttachmentsForQuestions ?? false,
    allowRecordLinksForQuestions: field.allowRecordLinksForQuestions ?? false,
    buttonCaption: field.buttonCaption?.translations?.[0]?.value ?? '',
    questions: sortedQuestions.map((question, index) => {
      const text = question.caption?.translations[0].value;
      return {
        ...question,
        value: question.id === text ? `newItem-${crypto.randomUUID()}` : question.id,
        text: text,
        id: question.id,
        position: index,
      };
    }),
    answers:
      sortedAnswers.length > 0
        ? sortedAnswers.map((answer, index) => {
            const text = answer.caption?.translations[0].value;
            return {
              ...answer,
              value: answer.id === text ? `newItem-${crypto.randomUUID()}` : answer.id,
              text: text,
              position: index,
              id: answer.id,
            };
          })
        : [],
    displayScores: selectedDisplayScoresOption,
  };
};
